import { useState, useEffect } from 'react';
import { Save, TrendingUp, TrendingDown, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase, EntryType } from '../lib/supabase';
import { translateEntryTypeName } from '../lib/translateEntryType';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

type NewLedgerProps = {
  onBack: () => void;
  onSave: () => void;
};

export default function NewLedger({ onBack, onSave }: NewLedgerProps) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [entryTypes, setEntryTypes] = useState<EntryType[]>([]);
  const [date, setDate] = useState('');
  const [notes, setNotes] = useState('');
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    loadEntryTypes();
    setDate(getNextSaturday());
  }, []);

  const loadEntryTypes = async () => {
    const { data, error } = await supabase
      .from('entry_types')
      .select('*')
      .order('display_order');

    if (!error && data) {
      setEntryTypes(data);
    }
  };

  const getNextSaturday = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();

    if (dayOfWeek === 6) {
      return today.toISOString().split('T')[0];
    }

    const daysUntilSaturday = (6 - dayOfWeek + 7) % 7;
    const nextSaturday = new Date(today);
    nextSaturday.setDate(today.getDate() + (daysUntilSaturday === 0 ? 7 : daysUntilSaturday));

    return nextSaturday.toISOString().split('T')[0];
  };

  const handleAmountChange = (entryTypeId: string, value: string) => {
    setAmounts(prev => ({
      ...prev,
      [entryTypeId]: value
    }));
  };

  const calculateTotals = () => {
    let totalIncome = 0;
    let totalExpenses = 0;

    entryTypes.forEach(type => {
      const amount = parseFloat(amounts[type.id] || '0') || 0;
      if (type.entry_category === 'income') {
        totalIncome += amount;
      } else {
        totalExpenses += amount;
      }
    });

    return {
      income: totalIncome,
      expenses: totalExpenses,
      net: totalIncome - totalExpenses
    };
  };

  const totals = calculateTotals();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data: newLedger, error: createError } = await supabase
        .from('weekly_ledgers')
        .insert({
          date,
          notes,
          created_by: user?.id
        })
        .select()
        .single();

      if (createError) throw createError;

      const entries = entryTypes.map(type => ({
        weekly_ledger_id: newLedger.id,
        entry_type_id: type.id,
        amount: parseFloat(amounts[type.id] || '0')
      }));

      const { error: entriesError } = await supabase
        .from('ledger_entries')
        .insert(entries);

      if (entriesError) throw entriesError;

      onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save ledger');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-slate-800">{t('ledgerForm.titleNew')}</h1>
          <p className="text-slate-600 mt-1">{t('dashboard.subtitle')}</p>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('ledgerForm.date')}
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('ledgerForm.notesOptional')}
              </label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                placeholder={t('ledgerForm.notesPlaceholder')}
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">{t('ledgerForm.entryAmounts')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {entryTypes.map((type) => (
              <div key={type.id}>
                <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                  {type.entry_category === 'income' ? (
                    <TrendingUp className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <TrendingDown className="w-4 h-4 text-red-600" />
                  )}
                  {translateEntryTypeName(type.name, language)}
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={amounts[type.id] || ''}
                    onChange={(e) => handleAmountChange(type.id, e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                    placeholder="0.00"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="space-y-3">
            <div className="flex items-center justify-between pb-2">
              <span className="text-sm font-medium text-slate-600 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
                {t('ledgerForm.totalIncome')}
              </span>
              <span className="text-lg font-semibold text-emerald-600">
                £{totals.income.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
            <div className="flex items-center justify-between pb-2">
              <span className="text-sm font-medium text-slate-600 flex items-center gap-2">
                <TrendingDown className="w-4 h-4 text-red-600" />
                {t('ledgerForm.totalExpenses')}
              </span>
              <span className="text-lg font-semibold text-red-600">
                £{totals.expenses.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-slate-300">
              <span className="text-lg font-semibold text-slate-800">{t('ledgerForm.netTotal')}</span>
              <span className={`text-2xl font-bold ${
                totals.net >= 0 ? 'text-emerald-600' : 'text-red-600'
              }`}>
                £{totals.net.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={onBack}
            className="flex-1 px-6 py-3 border border-slate-300 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-colors"
          >
            {t('ledgerForm.cancel')}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-400 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <Save className="w-5 h-5" />
            {loading ? t('ledgerForm.saving') : t('ledgerForm.save')}
          </button>
        </div>
      </form>
    </div>
  );
}
